import { GraphQLClient } from "graphql-request";
import {
  SalesReferenceDocumentType,
  SalesReturnFindUniqueQuery,
} from "../../../generated/sales";
import { ISalesReturn } from "../../../types/Sales/salesReturn";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { ITEM_SKU_DETAIL_AGGRID } from "../../../services/AgGrid/InventoryAggrid";
import { ISalesItemList } from "../../../types/Sales";
import dayjs from "dayjs";
import { IApprovalTemplate, IReviewedBy } from "../../../types/global";
import { v4 as uuidv4 } from "uuid";

export const salesReturnCreatePayloadFormatter = (
  data: ISalesReturn,
  status: string,
  approval_step?: number,
  reviewed_by?: IReviewedBy
) => {
  const {
    item_list,
    customer_details,
    tag_list,
    created_date,
    item_price_list,
    sales_channel,
    ...otherData
  } = data;
  const formatTagList = tag_list ? tag_list.map((tag: any) => tag.id) : [];
  const formatItemList = item_list.map(
    ({ uom_group, item_sku_qty, ...otherItem }) => ({
      ...otherItem,
      reference_document_type: SalesReferenceDocumentType.SalesReturn,
      reference_unique_id: data.unique_id,
    })
  );

  const { unique_id_name, ...customer } = customer_details;

  const formatPayload = {
    ...otherData,
    customer_details: customer,
    item_list: formatItemList,
    main_status: status,
    sub_status: status,
    tag_id_list: formatTagList,
    approval_step: approval_step ? 1 : 0,
    reviewer_list: reviewed_by ? [reviewed_by] : [],
  };
  return formatPayload;
};

export const salesReturnUpdatePayloadFormatter = (
  data: ISalesReturn,
  status: string,
  approval_step?: number,
  approver_list?: IApprovalTemplate[],
  isNotApprove?: boolean,
  reviewed_by?: IReviewedBy
) => {
  const {
    id,
    main_status,
    aggrid_status,
    flag_status,
    customer_details,
    last_updated_date,
    last_updated_by,
    item_list,
    tag_list,
    created_by,
    created_date,
    branch,
    sales_channel,
    reviewer_list,
    item_price_list,
    ...otherData
  } = data;
  const { unique_id_name, ...customer } = customer_details;

  const formatItemList = item_list.map(
    ({ uom_group, item_sku_qty, ...otherItem }) => ({
      ...otherItem,
      reference_document_type: SalesReferenceDocumentType.SalesReturn,
      reference_unique_id: data.unique_id,
    })
  );

  const formatTagList = tag_list ? tag_list.map((tag: any) => tag.id) : [];

  const formatFlag =
    !isNotApprove && (status === "approved" || status === "wait_approve")
      ? flag_status?.filter((flag) => flag !== "not_approved")
      : flag_status;

  const formatPayload = {
    ...otherData,
    approval_step: approval_step ? approval_step : 0,
    approver_list:
      approver_list && approver_list.length > 0
        ? approver_list
        : data.approver_list,
    reviewer_list: reviewer_list
      ? data.aggrid_status === "wait_approve" && reviewed_by
        ? !isNotApprove
          ? [...reviewer_list, reviewed_by]
          : [...reviewer_list]
        : data.aggrid_status === "not_approved" && status === "wait_approve"
        ? []
        : [...reviewer_list]
      : [],
    customer_details: customer,
    item_list: formatItemList,
    tag_id_list: formatTagList,
    main_status: status,
    sub_status: status,
    flag_status: formatFlag,
  };
  return formatPayload;
};

export const salesReturnQueryFormatter = async (
  data: SalesReturnFindUniqueQuery["SalesReturnFindUnique"]
) => {
  const graphQLClientWithHeaderItem: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");
  const allItemListUniqueId =
    data.item_list && data.item_list.length > 0
      ? data.item_list.map((item) => item?.item_sku_name)
      : [];

  const { itemSkuDetailsFindManyAggrid } =
    await graphQLClientWithHeaderItem.request(ITEM_SKU_DETAIL_AGGRID, {
      aggridInput: {
        startRow: 0,
        endRow: 9999,
        filterModel: {
          sku_name: {
            filterType: "set",
            values: allItemListUniqueId,
          },
        },
      },
    });

  const { results: itemSkuDetails } = await itemSkuDetailsFindManyAggrid;

  let formatItemList: ISalesItemList[] = [];

  if (data.item_list && data.item_list.length > 0) {
    data.item_list.forEach((item) => {
      const foundItemIndex = itemSkuDetails.findIndex(
        (realItem: any) => realItem.sku_name === item?.item_sku_name
      );

      const itemType = item as ISalesItemList;

      formatItemList.push({
        ...itemType,
        uom_group: itemSkuDetails[foundItemIndex]?.item.uom_group,
        item_sku_qty: itemSkuDetails[foundItemIndex]?.total_sku_qty,
      });
    });
  }

  const formatCustomer = {
    ...data.customer_details,
    unique_id_name: `${data.customer_unique_id} - ${data.customer_details.name}`,
  };

  const formatPayload = {
    ...data,
    item_price_list_id: data.item_price_list_id || 0,
    customer_details: formatCustomer,
    item_list: formatItemList,
    created_date: data.created_date ? dayjs(data.created_date) : undefined,
    issue_date: data.issue_date ? dayjs(data.issue_date) : undefined,
    due_date: data.due_date ? dayjs(data.due_date) : undefined,
  } as ISalesReturn;

  return formatPayload;
};

export const copySalesReturnFormatter = (data: ISalesReturn) => {
  if (data) {
    const {
      id,
      unique_id,
      created_date,
      issue_date,
      due_date,
      created_by,
      aggrid_status,
      main_status,
      sub_status,
      flag_status,
      item_list,
      last_updated_date,
      branch,
      sales_channel,
      approval_step,
      approver_list,
      reviewer_list,
      external_reference_no,
      ...otherData
    } = data;

    const formatItemList =
      item_list && item_list.length > 0
        ? item_list.map(
            ({ id, unique_id, reference_unique_id, ...otherItemList }) => ({
              ...otherItemList,
              unique_id: uuidv4(),
            })
          )
        : [];

    return {
      ...otherData,
      item_list: formatItemList,
      copied_id: id,
      copied_unique_id: unique_id,
    };
  }
};

export const createGoodsReturnFromReturn = (data: ISalesReturn) => {
  return data;
};
